/**
 * 智能体内容管理器
 * @author Joey
 */

import { useToast } from "@/hooks/use-toast";
import { PushContentData, pushContentList } from "@/services/agent-v2-push-content-list";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserManagementHooks } from "./agent-dashboard-user-management-manager";
import { agentV2UserCreationStats, AgentV2UserCreationStatsData } from "@/services/agent-v2-user-creation-stats";
import { agentV2PushContentDetail, AgentV2PushContentDetailData } from "@/services/agent-v2-push-content-detail";

// 缓存数据结构
export interface SearchFormData {
  searchType: string;
  searchValue: string;
  type: number;
  source: number;
  pushState: number;
  startTime: string;
  endTime: string;
  page: number;
  pageSize: number;
  total?: number;
}

type ContentListChangeEventDetail = {
  contentList: PushContentData[];
};
type ContentListChangeEvent = CustomEvent<ContentListChangeEventDetail>;

type SearchFormChangeEventDetail = SearchFormData;
type SearchFormChangeEvent = CustomEvent<SearchFormChangeEventDetail>;

type ContentStatsChangeEventDetail = AgentV2UserCreationStatsData
type ContentStatsChangeEvent = CustomEvent<ContentStatsChangeEventDetail>;

type ContentDetailChangeEventDetail = AgentV2PushContentDetailData
type ContentDetailChangeEvent = CustomEvent<ContentDetailChangeEventDetail>;

export class AgentContentManager {
  private static instance: AgentContentManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_CONTENT_LIST";
  // 当前智能体ID
  private agentId: string = "";
  // 智能体内容列表
  private contentList: PushContentData[] = [];
  private contentDetail: AgentV2PushContentDetailData = {} as AgentV2PushContentDetailData;
  // 统计数据
  private contentStats = {
    total: 0,
    autoPush: 0,
    interactionRate: 0,
    // completedOrderCount: 0,
    // totalAmount: 0,
  }
  // 统计数据初始化状态
  private initializeStats = false;
  // 搜索表单的数据
  private searchForm: SearchFormData = {
    searchType: "",
    searchValue: "",
    type: 999,
    source: null,
    pushState: null,
    startTime: "",
    endTime: "",
    page: 1,
    pageSize: 10,
    total: 0,
  }

  private constructor() { }

  // 单例模式
  public static getInstance(): AgentContentManager {
    if (!AgentContentManager.instance) {
      AgentContentManager.instance = new AgentContentManager();
    }
    return AgentContentManager.instance;
  }

  // 初始化
  public async init(agentId: string, refresh: boolean = false) {
    this.setAgentId(agentId);
    try {
      // 初始化时，如果列表为空，才请求数据
      if (this.getContentList().length === 0 || refresh) {
        await this.fetchContentList();
      }
    } catch (error) {
      console.error("Failed to init agent content list cache:", error);
      throw error;
    }
  }

  // 请求列表数据
  public async fetchContentList(page?: number) {
    try {
      const data = await pushContentList({
        ...this.getSearchForm(),
        page: page ?? this.searchForm.page,
        agentId: this.getAgentId(),
      });
      this.setContentList(data.list);
      this.setSearchForm({
        ...this.searchForm,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
      });
    } catch (error) {
      console.error("Failed to fetch agent content list:", error);
      this.dispatchContentListChange();
      throw error;
    }
  }

  // 请求用户制作统计数据
  public async fetchContentStats(agentId: string) {
    try {
      const data = await agentV2UserCreationStats(agentId);
      this.setContentStats(data);
    } catch (error) {
      console.error("Failed to fetch agent user creation stats:", error);
      this.dispatchContentStatsChange();
      throw error;
    }
  }

  // 请求内容详情数据
  public async fetchContentDetail(id: string) {
    try {
      const data = await agentV2PushContentDetail(id);
      this.setContentDetail(data);
    } catch (error) {
      console.error("Failed to fetch agent push content detail:", error);
      this.dispatchContentDetailChange();
      throw error;
    }
  }

  // 重置列表和搜索表单
  public resetContentListAndSearchForm() {
    this.setContentList([]);
    this.setSearchForm({
      searchType: '',
      searchValue: '',
      type: 999,
      source: null,
      pushState: null,
      startTime: "",
      endTime: "",
      page: 1,
      pageSize: 10,
      total: 0,
    });
    // 重置统计数据
    this.setInitializeStats(false);
  }

  // ==================== 读取写入操作 ====================
  public getAgentId() {
    return this.agentId;
  }
  private setAgentId(agentId: string) {
    this.agentId = agentId;
  }

  public getContentList() {
    return this.contentList;
  }
  private setContentList(list: PushContentData[]) {
    this.contentList = list;
    this.dispatchContentListChange();
  }

  public getSearchForm() {
    return this.searchForm;
  }
  public setSearchForm(form: SearchFormData) {
    this.searchForm = form;
    this.dispatchSearchFormChange();
  }

  public getInitializeStats() {
    return this.initializeStats;
  }
  public setInitializeStats(initializeStats: boolean) {
    this.initializeStats = initializeStats;
  }

  public getContentStats() {
    return this.contentStats;
  }
  private setContentStats(stats: AgentV2UserCreationStatsData) {
    this.contentStats = stats;
    this.dispatchContentStatsChange();
  }

  public getContentDetail() {
    return this.contentDetail;
  }
  public setContentDetail(detail: AgentV2PushContentDetailData) {
    this.contentDetail = detail;
    this.dispatchContentDetailChange();
  }


  // ==================== 订阅通知操作 ====================
  // 监听列表变化事件
  private dispatchContentListChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<ContentListChangeEventDetail>(`${this.STORAGE_KEY}:list-change`, {
      detail: { contentList: this.contentList }
    });
    window.dispatchEvent(event);
  }
  public onContentListChange(listener: (e: ContentListChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:list-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:list-change`, listener);
  }

  // 监听搜索表单变化事件
  private dispatchSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<SearchFormChangeEventDetail>(`${this.STORAGE_KEY}:search-form-change`, {
      detail: this.searchForm
    });
    window.dispatchEvent(event);
  }
  public onSearchFormChange(listener: (e: SearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:search-form-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:search-form-change`, listener);
  }

  // 监听统计数据变化事件
  private dispatchContentStatsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<ContentStatsChangeEventDetail>(`${this.STORAGE_KEY}:stats-change`, {
      detail: this.contentStats
    });
    window.dispatchEvent(event);
  }
  public onContentStatsChange(listener: (e: ContentStatsChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:stats-change`, listener);
    return () => window.removeEventListener(this.STORAGE_KEY, listener);
  }

  // 监听内容详情变化事件
  private dispatchContentDetailChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<ContentDetailChangeEventDetail>(`${this.STORAGE_KEY}:detail-change`, {
      detail: this.contentDetail
    });
    window.dispatchEvent(event);
  }
  public onContentDetailChange(listener: (e: ContentDetailChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:detail-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:detail-change`, listener);
  }
}

// ============= Hooks =============
// 内容列表数据
const useContentListData = () => {
  const toast = useToast();
  const { id } = useParams();
  const _type = new URLSearchParams(window.location.search).get('type');

  const [contentList, setContentList] = useState(agentContentManager.getContentList());
  const [searchFormData, setSearchFormData] = useState(agentContentManager.getSearchForm());
  const [loading, setLoading] = useState(false);

  useUserManagementHooks.useListenerAfterAction('content');

  const init = async (refresh?: boolean) => {
    if (!id) {
      console.error('Agent id is required');
      return;
    }
    try {
      setLoading(true);
      await agentContentManager.init(id, refresh);
      setLoading(false);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const search = async (page?: number) => {
    setLoading(true);
    try {
      await agentContentManager.fetchContentList(page);
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  const changeSearchForm = (key: keyof SearchFormData, value: string | number | string[]) => {
    if (key === 'startTime') {
      const [startTime, endTime] = value as string[];
      agentContentManager.setSearchForm({
        ...searchFormData,
        startTime,
        endTime,
      });
      return;
    }
    agentContentManager.setSearchForm({
      ...searchFormData,
      [key]: value,
    });
  }

  const changePage = (page: number) => {
    changeSearchForm('page', page);
  }

  const resetAndSearch = async () => {
    agentContentManager.setSearchForm({
      searchType: "",
      searchValue: "",
      type: 999,
      source: null,
      pushState: null,
      startTime: "",
      endTime: "",
      page: 1,
      pageSize: 10,
      total: 0,
    });
    await search();
  }

  useEffect(() => {
    if (_type) {
      agentContentManager.setSearchForm({
        ...searchFormData,
        type: Number(_type),
      });
    }
    init(_type ? true : false);
  }, [_type]);

  useEffect(() => {
    const unsubscribe = agentContentManager.onContentListChange((e) => {
      setContentList(e.detail.contentList)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentContentManager.onSearchFormChange((e) => {
      setSearchFormData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    contentList,
    searchFormData,
    loading,
    search,
    changePage,
    resetAndSearch,
    changeSearchForm,
  }
}

// 内容统计数据
const useContentStats = () => {
  const toast = useToast();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [contentStatsData, setContentStatsData] = useState(agentContentManager.getContentStats());

  const init = async () => {
    if (!id) {
      console.error({
        description: `Agent id is required`,
      });
      return;
    }
    try {
      setLoading(true);
      // 初始化统计数据
      agentContentManager.setInitializeStats(true);
      await agentContentManager.fetchContentStats(id);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // 如果已经初始化了统计数据，就不需要再初始化
    if (agentContentManager.getInitializeStats()) return;
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentContentManager.onContentStatsChange((e) => {
      setContentStatsData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    contentStatsData,
  }
}

// 内容详情数据
const useContentDetail = () => {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [contentDetail, setContentDetail] = useState(agentContentManager.getContentDetail());

  const fetchDetail = async (id: string) => {
    try {
      setLoading(true);
      await agentContentManager.fetchContentDetail(id);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const resetDetail = () => {
    agentContentManager.setContentDetail({} as AgentV2PushContentDetailData);
  }

  useEffect(() => {
    const unsubscribe = agentContentManager.onContentDetailChange((e) => {
      setContentDetail(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    contentDetail,
    fetchDetail,
    resetDetail,
  }
}

export const agentContentHooks = {
  useContentListData,
  useContentStats,
  useContentDetail,
}

// 导出单例实例
export const agentContentManager = AgentContentManager.getInstance();
